import { useMemo } from 'react';
import {
  Search,
  User,
  Shield,
  ToggleLeft,
  Layers,
  ChevronsUp,
  ChevronUp,
  Minus,
  ChevronDown,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/Select';
import { SEVERITY_LABELS } from '@momentum/types';
import type { SignalFilters } from '../constants';
import { SignalActiveFilters } from './components/SignalActiveFilters';

interface SignalFilterBarProps {
  filters: SignalFilters;
  onFiltersChange: (filters: SignalFilters) => void;
  owners: Array<{ id: string; fullName: string; imageUrl?: string }>;
}

const severityOptions = [
  {
    value: SEVERITY_LABELS.Critical,
    icon: ChevronsUp,
    iconClass: 'text-red-600',
  },
  {
    value: SEVERITY_LABELS.High,
    icon: ChevronUp,
    iconClass: 'text-orange-600',
  },
  { value: SEVERITY_LABELS.Medium, icon: Minus, iconClass: 'text-yellow-600' },
  { value: SEVERITY_LABELS.Low, icon: ChevronDown, iconClass: 'text-gray-400' },
];

const triggerClassName =
  'min-h-[43px] h-[43px] border-gray-300 bg-white text-sm';

export const SignalFilterBar = ({
  filters,
  onFiltersChange,
  owners,
}: SignalFilterBarProps) => {
  const { t } = useTranslation('knowledge');

  const update = (patch: Partial<SignalFilters>) =>
    onFiltersChange({ ...filters, ...patch });

  // Active filter pills
  const activeFilterPills = useMemo(() => {
    const pills: Array<{ key: string; label: string }> = [];
    if (filters.ownerFilter) {
      const ownerName =
        owners.find((owner) => owner.id === filters.ownerFilter)?.fullName ??
        filters.ownerFilter;
      pills.push({ key: 'owner', label: `${t('filterOwner')}: ${ownerName}` });
    }
    if (filters.severityFilter !== null) {
      const label =
        filters.severityFilter >= SEVERITY_LABELS.Critical
          ? t('severityCritical')
          : filters.severityFilter >= SEVERITY_LABELS.High
          ? t('severityHigh')
          : filters.severityFilter >= SEVERITY_LABELS.Medium
          ? t('severityMedium')
          : t('severityLow');
      pills.push({
        key: 'severity',
        label: `${t('filterSeverity')}: ${label}`,
      });
    }
    if (filters.statusFilter) {
      pills.push({
        key: 'status',
        label: `${t('filterStatus')}: ${
          filters.statusFilter === 'active' ? t('active') : t('inactive')
        }`,
      });
    }
    if (filters.categoryFilter) {
      pills.push({
        key: 'category',
        label: `${t('filterCategory')}: ${
          filters.categoryFilter === 'COMMITMENT'
            ? t('categoryCommitment')
            : t('categoryPlaybook')
        }`,
      });
    }
    return pills;
  }, [
    filters.ownerFilter,
    filters.severityFilter,
    filters.statusFilter,
    filters.categoryFilter,
    owners,
    t,
  ]);

  const handleRemoveFilter = (key: string) => {
    if (key === 'owner') update({ ownerFilter: null });
    if (key === 'severity') update({ severityFilter: null });
    if (key === 'status') update({ statusFilter: null });
    if (key === 'category') update({ categoryFilter: null });
  };

  const handleClearAllFilters = () => {
    update({
      ownerFilter: null,
      severityFilter: null,
      statusFilter: null,
      categoryFilter: null,
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2.5">
        {/* Search */}
        <div className="relative min-w-[200px] max-w-[360px] flex-1">
          <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8c8078]" />
          <input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={filters.searchQuery}
            onChange={(e) => update({ searchQuery: e.target.value })}
            className="h-[43px] w-full rounded-md border border-gray-300 bg-white ps-9 pe-3 text-sm text-[#2c2520] placeholder:text-muted-foreground focus:outline-none"
          />
        </div>

        {/* Owner filter */}
        <Select
          value={filters.ownerFilter ?? '__all__'}
          onValueChange={(selectedValue) =>
            update({
              ownerFilter: selectedValue === '__all__' ? null : selectedValue,
            })
          }
        >
          <SelectTrigger className={`w-[160px] ${triggerClassName}`}>
            <span className="!flex items-center gap-1.5 truncate">
              <User className="h-3.5 w-3.5 shrink-0" />
              <SelectValue placeholder={t('filterOwner')} />
            </span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">{t('filterOwner')}</SelectItem>
            {owners.map((owner) => (
              <SelectItem key={owner.id} value={owner.id}>
                {owner.fullName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Severity filter */}
        <Select
          value={
            filters.severityFilter !== null
              ? String(filters.severityFilter)
              : '__all__'
          }
          onValueChange={(selectedValue) =>
            update({
              severityFilter:
                selectedValue === '__all__' ? null : Number(selectedValue),
            })
          }
        >
          <SelectTrigger className={`w-[150px] ${triggerClassName}`}>
            <span className="!flex items-center gap-1.5 truncate">
              {filters.severityFilter === null && (
                <Shield className="h-3.5 w-3.5 shrink-0" />
              )}
              <SelectValue placeholder={t('filterSeverity')} />
            </span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">{t('filterSeverity')}</SelectItem>
            {severityOptions.map((opt) => {
              const Icon = opt.icon;
              return (
                <SelectItem key={opt.value} value={String(opt.value)}>
                  <span className="flex items-center gap-2">
                    <Icon className={`h-3.5 w-3.5 shrink-0 ${opt.iconClass}`} />
                    {opt.value === SEVERITY_LABELS.Critical
                      ? t('severityCritical')
                      : opt.value === SEVERITY_LABELS.High
                      ? t('severityHigh')
                      : opt.value === SEVERITY_LABELS.Medium
                      ? t('severityMedium')
                      : t('severityLow')}
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        {/* Status filter */}
        <Select
          value={filters.statusFilter ?? '__all__'}
          onValueChange={(selectedValue) =>
            update({
              statusFilter: selectedValue === '__all__' ? null : selectedValue,
            })
          }
        >
          <SelectTrigger className={`w-[140px] ${triggerClassName}`}>
            <span className="!flex items-center gap-1.5 truncate">
              <ToggleLeft className="h-3.5 w-3.5 shrink-0" />
              <SelectValue placeholder={t('filterStatus')} />
            </span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">{t('filterStatus')}</SelectItem>
            <SelectItem value="active">{t('active')}</SelectItem>
            <SelectItem value="inactive">{t('inactive')}</SelectItem>
          </SelectContent>
        </Select>

        {/* Category filter */}
        <Select
          value={filters.categoryFilter ?? '__all__'}
          onValueChange={(selectedValue) =>
            update({
              categoryFilter:
                selectedValue === '__all__' ? null : selectedValue,
            })
          }
        >
          <SelectTrigger className={`w-[150px] ${triggerClassName}`}>
            <span className="!flex items-center gap-1.5 truncate">
              <Layers className="h-3.5 w-3.5 shrink-0" />
              <SelectValue placeholder={t('filterCategory')} />
            </span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">{t('filterCategory')}</SelectItem>
            <SelectItem value="COMMITMENT">
              {t('categoryCommitment')}
            </SelectItem>
            <SelectItem value="PLAYBOOK">{t('categoryPlaybook')}</SelectItem>
          </SelectContent>
        </Select>

        {/* Sort — pushed to end */}
        <div className="ms-auto flex items-center gap-1.5">
          <span className="text-xs text-[#8c8078]">{t('sortBy')}:</span>
          <Select
            value={filters.sortBy}
            onValueChange={(selectedValue) =>
              update({ sortBy: selectedValue as SignalFilters['sortBy'] })
            }
          >
            <SelectTrigger className={`w-[140px] ${triggerClassName}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">{t('sortNewest')}</SelectItem>
              <SelectItem value="severity">{t('sortSeverity')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <SignalActiveFilters
        filters={activeFilterPills}
        onRemove={handleRemoveFilter}
        onClearAll={handleClearAllFilters}
      />
    </div>
  );
};
